import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../common';
import { Typography } from '../../common/Typography';
import { Card } from '../../common/Card';
import { format, parseISO } from 'date-fns';
import { homeElevatedInset } from '../../home/homeDashboardStyles';
import { formatSessionTimeDisplay } from '../../../utils/homeSessionUtils';
import { interactiveBleedInsetX } from '../../../constants/interactiveSafeInsets';
import SessionDetailsModal from '../../sessions/SessionDetailsModal';
import { signupForSession, cancelSignup, getPlayerSignupStatus } from '../../../services/sessionManagementService';
import { ClubOverviewSection } from './ClubOverviewSection';
import { SessionTypeChip, sessionTypeTone } from './SessionTypeChip';

const carouselClass = `flex gap-4 overflow-x-auto pb-3 pt-1 ${interactiveBleedInsetX} scroll-smooth snap-x snap-mandatory sm:snap-none [scrollbar-gutter:stable_both-edges] touch-pan-x scrollbar-hide-mobile`;
const cardClass = 'shrink-0 w-[min(100%,18.5rem)] sm:w-72 lg:w-80 snap-start border-l-[3px]';

/**
 * Single upcoming session card — horizontal carousel card style.
 */
export function UpcomingSessionCard({ session, currentUser, clubId, canManage, onRefresh, compact = false }) {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [signupStatus, setSignupStatus] = useState(() => {
    const match = session.session_signups?.find((s) => s.player_id === currentUser?.id);
    if (!match) return null;
    return { signup_status: match.signup_status };
  });

  const isOngoing = session.session_status === 'ongoing';
  const isHost = session.host_player_id === currentUser?.id;
  const isSignedUp =
    signupStatus?.signup_status === 'confirmed' ||
    signupStatus?.signup_status === 'waitlist' ||
    session.session_players?.some((r) => r.player_id === currentUser?.id);

  const sessionDate = session.session_date ? format(parseISO(session.session_date), 'EEE, d MMM') : 'TBD';
  const sessionTime = formatSessionTimeDisplay(session.start_time);
  const confirmed = session.session_signups?.filter((s) => s.signup_status === 'confirmed').length ?? 0;
  const maxPl = session.max_players ?? 16;

  const statusLabel = isOngoing ? 'Live now' : 'Upcoming';
  const [bgClass, textClass] = isOngoing
    ? ['bg-emerald-100 dark:bg-emerald-900/30', 'text-emerald-700 dark:text-emerald-400']
    : ['bg-sky-100 dark:bg-sky-900/30', 'text-sky-700 dark:text-sky-400'];
  const typeTone = sessionTypeTone(session);

  const handleRefreshSignup = async () => {
    if (!currentUser?.id) return;
    try {
      const s = await getPlayerSignupStatus(session.id, currentUser.id);
      setSignupStatus(s);
      onRefresh?.();
    } catch (err) {
      console.warn('UpcomingSessionCard signup refresh:', err);
    }
  };

  const handleSignup = async (sessionId) => {
    if (!currentUser?.id) return;
    await signupForSession(sessionId, currentUser.id);
    await handleRefreshSignup();
  };

  const handleCancelSignup = async (sessionId) => {
    if (!currentUser?.id) return;
    await cancelSignup(sessionId, currentUser.id);
    await handleRefreshSignup();
  };

  return (
    <>
      <Card
        variant="default"
        size={compact ? 'small' : 'medium'}
        className={`flex flex-col ${compact ? 'gap-2 shadow-none bg-surface-subtle/40' : `gap-3 ${homeElevatedInset}`} ${typeTone.accent} ${isOngoing ? 'ring-2 ring-emerald-500/35 border-emerald-400/60 dark:border-emerald-600/50' : ''} ${cardClass}`}
      >
        {/* Status + session type */}
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`inline-flex items-center justify-center rounded-lg shrink-0 ${compact ? 'w-8 h-8' : 'w-9 h-9 rounded-xl'} ${bgClass} ${textClass}`}
              aria-hidden
            >
              {isOngoing ? (
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </span>
            <Typography variant="caption" className="text-text-muted uppercase tracking-wide text-[0.65rem] font-semibold">
              {statusLabel}
            </Typography>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {isSignedUp && !isOngoing ? (
              <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full bg-success/10 text-success border border-success/20">
                ✓ Signed up
              </span>
            ) : null}
            <SessionTypeChip session={session} />
          </div>
        </div>

        {/* Session name + meta */}
        <div className="flex flex-col gap-0.5 min-w-0">
          <Typography className={`font-semibold leading-snug text-text-primary ${compact ? 'text-xs line-clamp-1' : 'text-sm line-clamp-2'}`}>
            {session.name || 'Session'}
          </Typography>
          <Typography variant="caption" className="text-text-secondary leading-snug line-clamp-1">
            {sessionDate}{sessionTime ? ` · ${sessionTime}` : ''} · {session.signup_selection === 'host_select' ? `Up to ${maxPl} players` : `${confirmed}/${maxPl} players`}
          </Typography>
        </div>

        {/* CTA */}
        <div className="mt-auto">
          {isOngoing ? (
            <Button
              variant="success"
              size="small"
              className="w-full text-xs font-semibold gap-2"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/club/${clubId}/session/${session.id}`);
              }}
            >
              <span className="h-2 w-2 rounded-full bg-white animate-pulse shrink-0" aria-hidden />
              Open live session
            </Button>
          ) : isHost || canManage ? (
            <Button
              variant="outline"
              size="small"
              className="w-full text-xs font-semibold"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(true);
              }}
            >
              Manage session
            </Button>
          ) : (
            <Button
              variant={isSignedUp ? 'outline' : 'primary'}
              size="small"
              className="w-full text-xs font-semibold"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(true);
              }}
            >
              {isSignedUp ? 'View details' : 'View & join'}
            </Button>
          )}
        </div>
      </Card>

      {showModal && session?.id && currentUser && (
        <SessionDetailsModal
          sessionId={session.id}
          isOpen={showModal}
          onClose={() => { setShowModal(false); void handleRefreshSignup(); }}
          onSignup={handleSignup}
          onCancelSignup={handleCancelSignup}
          currentUser={currentUser}
          userSignupStatus={signupStatus}
        />
      )}
    </>
  );
}

/**
 * Upcoming sessions carousel for My Club overview.
 * Heading lives inside the section container; all sessions swipeable on mobile.
 */
export function ClubUpcomingSessionsCarousel({
  sessions,
  currentUser,
  clubId,
  canManage,
  onRefresh,
  onViewAll,
  onSectionClick,
}) {
  const navigate = useNavigate();

  const handleViewAll = onViewAll ?? (() => navigate(`/clubs/${clubId}?tab=club`));
  const handleSectionNav = onSectionClick ?? handleViewAll;

  if (!sessions || sessions.length === 0) {
    return (
      <ClubOverviewSection
        label="Upcoming session"
        variant="upcoming"
        subtitle="No sessions scheduled yet"
        onViewAll={handleViewAll}
        onSectionClick={handleSectionNav}
      >
        <div className="text-center py-6 rounded-xl border border-border-medium bg-surface-subtle/60">
          <Typography variant="muted">No upcoming sessions scheduled.</Typography>
        </div>
      </ClubOverviewSection>
    );
  }

  return (
    <ClubOverviewSection
      label={sessions.length === 1 ? 'Upcoming session' : 'Upcoming sessions'}
      variant="upcoming"
      onViewAll={handleViewAll}
      onSectionClick={handleSectionNav}
    >
      <div
        role="region"
        aria-label="Upcoming sessions"
        className={carouselClass}
        onClick={(e) => e.stopPropagation()}
      >
        {sessions.slice(0, 5).map((session) => (
          <UpcomingSessionCard
            key={session.id}
            session={session}
            currentUser={currentUser}
            clubId={clubId}
            canManage={canManage}
            onRefresh={onRefresh}
          />
        ))}
      </div>
      {sessions.length > 1 && (
        <Typography variant="caption" className="mt-3 text-center text-text-muted sm:hidden">
          Swipe for more sessions
        </Typography>
      )}
    </ClubOverviewSection>
  );
}
